import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { AnyBoomerFormApi } from "@/form/boomer/types";
import { CustomFieldFile } from "@/form/components/custom-field-file";
import { CustomFieldInput } from "@/form/components/custom-field-input";
import { CustomFieldMultiSelect } from "@/form/components/custom-field-multi-select";
import { CustomFieldSelect } from "@/form/components/custom-field-select";
import { CustomFieldToggle } from "@/form/components/custom-field-toggle";
import { api } from "@/lib/backend";
import type { CustomFieldDefinition } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { isEmptyCustomFieldValue, isPendingFileValue } from "@/lib/custom-fields/build-value-input";
import { logger } from "@/logger";

type CustomFieldFormValue = Record<string, any>;

interface ApplicationCustomFieldsProps {
  projectId: string;
  form: any; // First element from useBoomerForm tuple
}

/**
 * Renders APPLICATION context custom fields for a specific project. Fetches custom field
 * definitions with APPLICATION context and renders appropriate inputs.
 */
export function ApplicationCustomFields({ projectId, form }: ApplicationCustomFieldsProps) {
  const { t } = useTranslation();
  // Fetch APPLICATION custom fields for this project
  const { data: customFieldDefinitions, isLoading } = useQuery({
    queryKey: ["custom-field-definitions", "APPLICATION", projectId],
    queryFn: () =>
      extractionWrapper(api.customFieldDefinitions.getAll("APPLICATION", { projectId })),
    enabled: !!projectId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <p className="text-sm text-txt-secondary">Velden laden...</p>
      </div>
    );
  }

  if (!customFieldDefinitions || customFieldDefinitions.length === 0) {
    return null;
  }

  logger.info("Rendering APPLICATION custom fields", {
    projectId,
    fieldCount: customFieldDefinitions.length,
  });

  const renderField = (fieldDef: CustomFieldDefinition) => {
    const { id, name, type, required, options, description } = fieldDef;

    switch (type) {
      case "TEXT":
        return (
          <form.Field
            key={id}
            name={id}
            validators={{
              onChange: ({ value }: any) => {
                if (required && !value) {
                  return t("common:validation.required");
                }
                return undefined;
              },
            }}
            children={(field: any) => (
              <CustomFieldInput
                field={field}
                label={name}
                description={description}
                required={required}
              />
            )}
          />
        );

      case "NUMBER":
        return (
          <form.Field
            key={id}
            name={id}
            validators={{
              onChange: ({ value }: any) => {
                // Only check for null/undefined, not empty string
                // 0 is a valid number value
                if (required && (value === null || value === undefined)) {
                  return t("common:validation.required");
                }
                return undefined;
              },
            }}
            children={(field: any) => (
              <CustomFieldInput
                field={field}
                label={name}
                description={description}
                type="number"
                required={required}
              />
            )}
          />
        );

      case "DATE":
        return (
          <form.Field
            key={id}
            name={id}
            validators={{
              onChange: ({ value }: any) => {
                if (required && !value) {
                  return t("common:validation.required");
                }
                return undefined;
              },
            }}
            children={(field: any) => (
              <CustomFieldInput
                field={field}
                label={name}
                description={description}
                type="date"
                required={required}
              />
            )}
          />
        );

      case "SELECT":
        return (
          <form.Field
            key={id}
            name={id}
            validators={{
              onChange: ({ value }: any) => {
                if (required && !value) {
                  return t("common:validation.required");
                }
                return undefined;
              },
            }}
            children={(field: any) => (
              <CustomFieldSelect
                field={field}
                label={name}
                description={description}
                required={required}
                options={
                  options?.map((opt) => ({
                    label: opt.label,
                    value: opt.value,
                  })) ?? []
                }
              />
            )}
          />
        );

      case "MULTI_SELECT":
        return (
          <form.Field
            key={id}
            name={id}
            validators={{
              onChange: ({ value }: any) => {
                if (required && (!value || value.length === 0)) {
                  return t("common:validation.required");
                }
                return undefined;
              },
            }}
            children={(field: any) => (
              <CustomFieldMultiSelect
                field={field}
                label={name}
                description={description}
                required={required}
                options={
                  options?.map((opt) => ({
                    label: opt.label,
                    value: opt.value,
                  })) ?? []
                }
              />
            )}
          />
        );

      case "BOOLEAN":
        return (
          <form.Field
            key={id}
            name={id}
            validators={{
              onChange: ({ value }: any) => {
                if (required && value !== true && value !== false) {
                  return t("common:validation.required");
                }
                return undefined;
              },
            }}
            children={(field: any) => (
              <CustomFieldToggle
                field={field}
                label={name}
                description={description}
                required={required}
              />
            )}
          />
        );

      case "FILE":
        return (
          <form.Field
            key={id}
            name={id}
            validators={{
              onChange: ({ value }: any) => {
                // Right after picking a file the value is still a raw File while it is being read.
                // Don't flash "verplicht" during that window; submit blocks on it separately.
                if (isPendingFileValue("FILE", value)) {
                  return undefined;
                }
                // A raw File never reaches the api, so it must not pass as "filled in" (#117).
                if (required && isEmptyCustomFieldValue("FILE", value)) {
                  return t("common:validation.required");
                }
                return undefined;
              },
            }}
            children={(field: any) => (
              <CustomFieldFile
                field={field}
                label={name}
                description={description}
                required={required}
                // Turns the chosen file into a base64 data-URL so it can be sent as JSON with
                // the application, the same way the registration flow does it (#117).
                base64={true}
              />
            )}
          />
        );

      default:
        logger.warn("Unknown custom field type", { type, fieldId: id });
        return null;
    }
  };

  return <div className="flex flex-col gap-4">{customFieldDefinitions.map(renderField)}</div>;
}
